import { Question } from "../../type";
import { focusCell, clearRange } from "../common";
import { headerBlank, moveQuestion, pageBreaks } from "./move";
import { removeQuestionById } from "./clear";
import { getQuestionRange, getLastActiveRow, getQuestionIds } from "./excelInfo";
import { checkDiffAddinExcel } from "./compare";

export async function insertPageBreak(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  row: number,
  direction: "forward" | "backward"
) {
  const rangeTextTmp = `C${row}:N${row}`;
  const rangeTextInsert = direction == "forward" ? `C${row + 1}:N${row + 1}` : `C${row}:N${row}`;

  const pageBreakRange = sheet.getRange(rangeTextTmp);
  pageBreakRange.load("values");
  await context.sync();

  try {
    if (!pageBreaks.includes(pageBreakRange.values[0][1])) {
      sheet.getRange(rangeTextInsert).getEntireRow().insert(Excel.InsertShiftDirection.down);
      // スタイル消去
      // sheet.getRange(rangeTextInsert).clear(Excel.ClearApplyTo.formats);
      await context.sync();

      const pageBreakRangeNew = sheet.getRange(rangeTextInsert);
      pageBreakRangeNew.load("values");
      await context.sync();

      pageBreakRangeNew.values = [["", "ページの始まり", "", "", "", "", "", "", "", "", "", ""]];
      await context.sync();
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("ページブレークの挿入中にエラーが発生しました:", error);
    return false;
  }
}

export async function insertQuestionToExcel(
  sheetName: string,
  question: Question,
  nextQuestion?: Question
) {
  await Excel.run(async (context) => {
    // シートの値を読み込む
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const usedRange = sheet.getRange(`C${headerBlank + 1}:N9999`);
    usedRange.load("values, rowCount");
    await context.sync();

    const { rangeText, firstRowIndex, firstRowIndexNext, isLastQuestion, questionRowsCount } =
      await getQuestionRange(question, nextQuestion?.questionId || "", sheetName);

    const pageBreakInfo = [question.forwardPageBreak, nextQuestion?.forwardPageBreak || false];

    const startRow = headerBlank + firstRowIndex + 1;
    let addedRows = 0;

    if (isLastQuestion) {
      // 該当範囲のデータを消去（空白で埋める）
      const targetRangeAddress = `C${startRow}:N${startRow + questionRowsCount - 1}`;
      await clearRange(context, sheet, targetRangeAddress);

      // 貼り付け
      await pasteQuestionValue(context, sheet, question, startRow);

      const {
        firstRowIndex: firstRowIndexTmp,
        questionRowsCount: questionRowsCountTmp,
        lastActiveRow,
      } = await getQuestionRange(question, nextQuestion?.questionId, sheetName);

      const startRowTmp = firstRowIndexTmp + headerBlank + questionRowsCountTmp + 1;
      if (startRowTmp < lastActiveRow) {
        const deleteRangeAddress = `A${startRowTmp}:A${lastActiveRow}`;
        sheet.getRange(deleteRangeAddress).getEntireRow().delete(Excel.DeleteShiftDirection.up);
        await context.sync();
      }
    } else {
      // もし不足していたら、行を挿入（& 貼り付けセル範囲を更新）
      if (questionRowsCount > firstRowIndexNext - firstRowIndex) {
        const rowsToInsert = questionRowsCount - (firstRowIndexNext - firstRowIndex);
        const insertRangeAddress = `A${startRow}:A${headerBlank + firstRowIndex + rowsToInsert}`;
        sheet.getRange(insertRangeAddress).getEntireRow().insert(Excel.InsertShiftDirection.down);
        await context.sync();
      }

      // 該当範囲のデータを消去（空白で埋める）
      const targetRangeAddress = `C${startRow}:N${headerBlank + firstRowIndexNext - 1}`;
      await clearRange(context, sheet, targetRangeAddress);

      // 貼り付け
      await pasteQuestionValue(context, sheet, question, startRow);

      // もし余っていたら、行を削除
      if (questionRowsCount < firstRowIndexNext - firstRowIndex - (pageBreakInfo[1] ? 1 : 0)) {
        const deleteRangeAddress = `A${headerBlank + firstRowIndex + questionRowsCount + 1}:A${headerBlank + firstRowIndexNext - 1}`;
        sheet.getRange(deleteRangeAddress).getEntireRow().delete(Excel.DeleteShiftDirection.up);
        await context.sync();
      }

      // ページ改定の確認＆挿入
      if (pageBreakInfo[0]) {
        const inserted = await insertPageBreak(context, sheet, startRow - 1, "forward");
        if (inserted) {
          addedRows += 1;
        }
      }

      if (pageBreakInfo[1]) {
        await insertPageBreak(context, sheet, startRow + addedRows + questionRowsCount, "backward");
      }
    }

    // if (isLastQuestion) {
    // } else if (pageBreakInfo[1]) {
    //   await insertPageBreak(context, sheet, startRow + addedRows + questionRowsCount, "backward");
    // }
  });
}

export async function insertQuestionBottom(sheetName: string, question: Question) {
  // 改ページ入れる

  const rangeText = await Excel.run(async (context) => {
    // シートの値を読み込む
    const sheet = context.workbook.worksheets.getItem(sheetName);

    // 末尾の行を取得
    const lastActiveRow = await getLastActiveRow(sheetName);

    // 最後の行の次の行に挿入
    const insertRow = lastActiveRow == headerBlank ? lastActiveRow + 2 : lastActiveRow;
    let rowCounts = await pasteQuestionValue(context, sheet, question, insertRow);

    // ページ改定の確認＆挿入
    if (question.forwardPageBreak) {
      await insertPageBreak(context, sheet, insertRow - 1, "forward");
    }

    // 5行目のスタイルだけをコピーして、貼り付け箇所に適用
    const sourceRange = sheet.getRange(`B${6}:N${6}`);
    const targetRange = question.forwardPageBreak
      ? sheet.getRange(`B${insertRow - 1}:N${insertRow + rowCounts + 1}`)
      : sheet.getRange(`B${insertRow}:N${insertRow + rowCounts + 1}`);
    targetRange.copyFrom(sourceRange, "Formats", false, false);

    await context.sync();

    if (lastActiveRow == headerBlank) {
      const targetRangeDelete = sheet.getRange(`B${headerBlank + 1}:N${headerBlank + 1}`);
      await targetRangeDelete.getEntireRow().delete(Excel.DeleteShiftDirection.up);
      await context.sync();

      return `B${insertRow - 1}`;
    }

    return `B${insertRow}`;
  });

  await focusCell(rangeText, sheetName);
}

export async function pasteQuestionValue(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  question: Question,
  firstRowIndex: number = 0,
  needHeaderBlank: boolean = false
) {
  let insertRow = (needHeaderBlank ? headerBlank : 0) + firstRowIndex;

  // 補足文の挿入
  if (question.questionSupplementPrev.length) {
    const supplementRange = sheet.getRange(
      `C${insertRow}:N${insertRow + question.questionSupplementPrev.length - 1}`
    );
    supplementRange.values = question.questionSupplementPrev.map((supplement) => {
      return ["", "設問補足文", "", "", supplement.text, "", "", "", "", "", "", ""];
    });
    insertRow += question.questionSupplementPrev.length;
  }

  // questionText、questionType、questionTitleを挿入
  const questionTextRange = sheet.getRange(`C${insertRow}:N${insertRow}`);
  questionTextRange.values = [
    [
      question.questionId,
      question.questionType,
      "",
      "",
      question.questionText,
      "",
      question.condition,
      "",
      "",
      "",
      question.remarks,
      question.questionTitle,
    ],
  ];
  insertRow += 1;

  // 補足文
  if (question.questionSupplement.length) {
    const supplementRange = sheet.getRange(`C${insertRow}:N${insertRow}`);
    supplementRange.values = [
      ["", "設問補足文", "", "", question.questionSupplement[0].text, "", "", "", "", "", "", ""],
    ];
    insertRow += 1;
  }

  // loop(ループ設問を挿入)
  if (question.loop.length > 0) {
    const loopRange = sheet.getRange(`C${insertRow}:N${insertRow + question.loop.length - 1}`);
    loopRange.values = question.loop.map((loopObj) => {
      return [
        "",
        "",
        "項目(表側)",
        loopObj.itemId,
        loopObj.text,
        "",
        loopObj.condition,
        "",
        loopObj.random,
        "",
        loopObj.remarks,
        "",
      ];
    });
    insertRow += question.loop.length;
  }

  // items(選択肢を挿入)
  const optionRowCount = question.options.length + question.optionHeaders.length;

  const itemRange = sheet.getRange(`C${insertRow}:N${insertRow + optionRowCount - 1}`);

  const itemValues = [];

  if (question.optionHeaders.length) {
    question.optionHeaders.forEach((header) => {
      itemValues.push([
        "",
        "",
        "",
        "小見出し",
        header.label,
        "",
        "header.condition",
        "",
        header.random,
        "",
        "header.remarks",
        "",
      ]);

      question.options
        .filter((_, index) => index + 1 >= header.from && index + 1 <= header.to)
        .forEach((option) => {
          itemValues.push([
            "",
            "",
            "",
            option.itemId,
            option.text,
            option.exclusive,
            option.condition,
            "",
            option.random,
            "",
            option.remarks,
            "",
          ]);
        });
    });
    itemRange.values = itemValues;
  } else {
    itemRange.values = question.options.map((option) => {
      return [
        "",
        "",
        "",
        option.itemId,
        option.text,
        option.exclusive,
        option.condition,
        "",
        option.random,
        "",
        option.remarks,
        "",
      ];
    });
  }

  insertRow += optionRowCount;
  await context.sync();

  return insertRow - ((needHeaderBlank ? headerBlank : 0) + firstRowIndex);
}

export async function resolveQuestionsDiff(questions, editSheet, onlyInAddin, onlyInExcel) {
  // Excelにはあるが、Addinにはない設問を非同期で削除
  const { excel: questionIdsExcel } = await getQuestionIds(questions, editSheet);

  for (const id of onlyInExcel) {
    const nextId = questionIdsExcel[questionIdsExcel.indexOf(id) + 1];
    await removeQuestionById(editSheet, id, nextId);
  }

  // Addinにはあるが、Excelにはない設問を非同期で追加
  for (const id of onlyInAddin) {
    await insertQuestionBottom(
      editSheet,
      questions.find((q) => q.questionId === id)
    );
  }

  // もし順番が異なる場合は、Excelの順番に合わせて並び替える
  const { moveStep } = await checkDiffAddinExcel(questions, editSheet);
  if (moveStep.length) {
    await Excel.run(async (context) => {
      // 一番上に行追加...
      const sheet = context.workbook.worksheets.getItem(editSheet);
      sheet
        .getRange(`A${headerBlank + 2}:A${headerBlank + 2}`)
        .getEntireRow()
        .insert(Excel.InsertShiftDirection.down);
      await context.sync();

      // B5:N5を、B6:N6にコピー
      const sourceRange = sheet.getRange(`B${headerBlank + 1}:N${headerBlank + 1}`);
      const targetRange = sheet.getRange(`B${headerBlank + 2}:N${headerBlank + 2}`);
      targetRange.copyFrom(sourceRange, "All", false, false);
    });

    for (const step of moveStep) {
      console.log(step);
      const [from, to] = step;
      const { excel } = await getQuestionIds(questions, editSheet);
      await moveQuestion(editSheet, excel[from], excel[from + 1], excel, to);
    }

    await Excel.run(async (context) => {
      // 一番上に行追加...
      const sheet = context.workbook.worksheets.getItem(editSheet);
      const targetRange = sheet.getRange(`D${headerBlank + 1}:D${headerBlank + 2}`);
      targetRange.load("values");

      await context.sync();

      if (
        pageBreaks.includes(targetRange.values[0][0]) &&
        pageBreaks.includes(targetRange.values[1][0])
      ) {
        // もしページブレークが2つ続いていたら、1つ削除
        sheet
          .getRange(`A${headerBlank + 1}:A${headerBlank + 1}`)
          .getEntireRow()
          .delete(Excel.DeleteShiftDirection.up);
      }
    });
  }
}
